import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { FiArrowUpRight, FiMail } from "react-icons/fi";
import { EmphasisText } from "@/components/helper/EmphasisText";
import { AppIcon } from "@/components/sections/Apps";
import { FAQ_ITEMS } from "@/config/faq";
import { APPS } from "@/config/apps";
import { getLatestPosts } from "@/utils/blog";
import { getSocialMediaLink } from "@/utils/external-links";
import type { DeviceSectionId } from "@/components/device/sections";

const PROOF_POINTS = ["solo", "volume", "mints", "certs"] as const;
const SERVICES = ["aiWorkflow", "aiAgent", "blockchain", "career"] as const;
const PROJECTS = [1, 2, 3, 4] as const;
const ROLES = [
    { title: "resume.foundingEngineer", company: "resume.omen", date: "resume.foundingEngineerDate" },
    { title: "resume.aiEngineer", company: "resume.animocaBrands", date: "resume.aiEngineerDate" },
    { title: "resume.juniorBlockchainEngineer", company: "resume.lexerMarkets", date: "resume.juniorBlockchainEngineerDate" },
    { title: "resume.juniorBackendDeveloper", company: "resume.bullBLimited", date: "resume.juniorBackendDeveloperDate" },
    { title: "resume.bachelorOfComputerEngineering", company: "resume.hku", date: "resume.bachelorOfComputerEngineeringDate" },
] as const;
/** The window is a preview; the full section holds the rest. */
const FAQ_PREVIEW = 5;
const POSTS_PREVIEW = 4;

/**
 * A section's content as a compact device window: the same copy and data the
 * full page section renders, trimmed to what fits on one screen.
 */
export function SectionWindowContent({ id, onOpenFull }: { id: DeviceSectionId; onOpenFull: () => void }) {
    const { t } = useTranslation();
    const { locale = "en" } = useParams<{ locale: string }>();
    const posts = useMemo(() => getLatestPosts(locale, POSTS_PREVIEW), [locale]);

    switch (id) {
        case "profile":
            return (
                <>
                    <h3 className="os-win-heading">{t("profile.motto")}</h3>
                    <p className="os-win-lede">
                        <EmphasisText i18nKey="profile.statement" />
                    </p>
                    <dl className="os-win-proof">
                        {PROOF_POINTS.map((point) => (
                            <div key={point}>
                                <dt>{t(`profile.proof.${point}.label`)}</dt>
                                <dd>{t(`profile.proof.${point}.value`)}</dd>
                            </div>
                        ))}
                    </dl>
                </>
            );
        case "services":
            return (
                <>
                    <h3 className="os-win-heading">{t("services.subtitle")}</h3>
                    <ul className="os-win-rows">
                        {SERVICES.map((service) => (
                            <li key={service}>
                                <strong>{t(`services.${service}.title`)}</strong>
                                <span>{t(`services.${service}.description`)}</span>
                            </li>
                        ))}
                    </ul>
                </>
            );
        case "portfolio":
            return (
                <>
                    <h3 className="os-win-heading">{t("portfolio.latestWorks")}</h3>
                    <ul className="os-win-rows">
                        {PROJECTS.map((n) => (
                            <li key={n}>
                                <strong>{t(`portfolio.project${n}Title`)}</strong>
                                <span>
                                    <EmphasisText i18nKey={`portfolio.project${n}Description`} />
                                </span>
                            </li>
                        ))}
                    </ul>
                </>
            );
        case "apps":
            return (
                <>
                    <h3 className="os-win-heading">{t("apps.subtitle")}</h3>
                    <ul className="os-win-apps">
                        {APPS.map((app) => (
                            <li key={app.id}>
                                <AppIcon app={app} size="sm" />
                                <span>
                                    <strong>{app.name}</strong>
                                    <span>{t(`apps.items.${app.id}.tagline`)}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </>
            );
        case "resume":
            return (
                <>
                    <h3 className="os-win-heading">{t("resume.subtitle")}</h3>
                    <ul className="os-win-rows is-timeline">
                        {ROLES.map((role) => (
                            <li key={role.title}>
                                <span className="os-win-meta">{t(role.date)}</span>
                                <strong>{t(role.title)}</strong>
                                <span>{t(role.company)}</span>
                            </li>
                        ))}
                    </ul>
                </>
            );
        case "blog":
            return (
                <>
                    <h3 className="os-win-heading">{t("blog.subtitle")}</h3>
                    <ul className="os-win-rows">
                        {posts.map((post) => (
                            <li key={post.slug}>
                                <Link className="os-win-link" to={`/${locale}/blog/${post.slug}`}>
                                    <strong>{post.title}</strong>
                                    <span>{post.excerpt}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <Link className="os-win-action" to={`/${locale}/blog`}>
                        {t("blog.viewAllPosts")}
                        <FiArrowUpRight aria-hidden="true" />
                    </Link>
                </>
            );
        case "faq":
            return (
                <>
                    <h3 className="os-win-heading">{t("faq.subtitle")}</h3>
                    <ul className="os-win-rows">
                        {FAQ_ITEMS.slice(0, FAQ_PREVIEW).map((item) => (
                            <li key={item.questionKey}>
                                <strong>{t(item.questionKey)}</strong>
                                <span>
                                    <EmphasisText i18nKey={item.answerKey} />
                                </span>
                            </li>
                        ))}
                    </ul>
                </>
            );
        case "contact":
            return (
                <>
                    <h3 className="os-win-heading">{t("contact.subtitle")}</h3>
                    <p className="os-win-lede">{t("contact.description")}</p>
                    <p className="os-win-meta">{t("contact.responseTime")}</p>
                    <div className="os-win-actions">
                        <button type="button" className="os-win-action is-primary" onClick={onOpenFull}>
                            {t("deviceOs.gate.writeMessage")}
                        </button>
                        <a className="os-win-action" href={`mailto:${getSocialMediaLink("email")}`}>
                            <FiMail aria-hidden="true" />
                            {getSocialMediaLink("email")}
                        </a>
                    </div>
                </>
            );
    }
}
